import React, { useEffect, useMemo, useRef, useState } from "react";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import SkipPreviousOutlinedIcon from "@mui/icons-material/SkipPreviousOutlined";
import SkipNextOutlinedIcon from "@mui/icons-material/SkipNextOutlined";
import FirstPageOutlinedIcon from "@mui/icons-material/FirstPageOutlined";
import LastPageOutlinedIcon from "@mui/icons-material/LastPageOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import FitScreenOutlinedIcon from "@mui/icons-material/FitScreenOutlined";

import "./linkAnalysis.scss";

const STORAGE_KEY = "wv_link_analysis_session_v1";
const GRAPH_WIDTH = 1600;
const GRAPH_HEIGHT = 900;
const MIN_EDGE_LENGTH = 125;
const MAX_EDGE_LENGTH = 265;

const NODE_TYPES = [
  "Person",
  "Residence",
  "Location",
  "Vehicle",
  "Organization",
  "Social Account",
  "Phone",
  "Email",
  "Event",
  "Other",
];

const CONNECTION_TYPES = [
  "Associate",
  "Friend",
  "Family",
  "Resides At",
  "Visited",
  "Works At",
  "Works With",
  "Owns",
  "Uses",
  "Follows",
  "Authorized",
  "Unauthorized",
  "Observed With",
  "Attended",
  "Other",
];

const TYPE_CLASS = {
  Person: "person",
  Residence: "residence",
  Location: "location",
  Vehicle: "vehicle",
  Organization: "organization",
  "Social Account": "social",
  Phone: "phone",
  Email: "email",
  Event: "event",
  Other: "other",
};

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
const normalizeName = (value = "") => String(value).trim().toLowerCase();
const boolish = (value) => ["true", "1", "yes", "y"].includes(String(value || "").trim().toLowerCase());
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const toLocalInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
};

const makeNode = (overrides = {}) => ({
  id: uid(),
  identifier: "",
  type: "Person",
  label: "",
  description: "",
  status: "",
  source: "",
  tag: "",
  mainTarget: false,
  hidden: false,
  x: GRAPH_WIDTH / 2 + (Math.random() - 0.5) * 220,
  y: GRAPH_HEIGHT / 2 + (Math.random() - 0.5) * 220,
  ...overrides,
});

const makeEdge = (overrides = {}) => ({
  id: uid(),
  from: "",
  to: "",
  connectionType: "Associate",
  start: "",
  end: "",
  directed: false,
  notes: "",
  evidence: "",
  ...overrides,
});

const blankState = () => ({ nodes: [], edges: [] });

const iconForType = (type) => {
  const map = {
    Person: PersonOutlineOutlinedIcon,
    Residence: HomeOutlinedIcon,
    Location: LocationOnOutlinedIcon,
    Vehicle: DirectionsCarOutlinedIcon,
    Organization: BusinessOutlinedIcon,
    "Social Account": AlternateEmailOutlinedIcon,
    Phone: PhoneOutlinedIcon,
    Email: AlternateEmailOutlinedIcon,
    Event: EventOutlinedIcon,
    Other: PublicOutlinedIcon,
  };
  return map[type] || PublicOutlinedIcon;
};

const parseCSV = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"' && quoted && next === '"') {
      field += '"';
      i += 1;
    } else if (ch === '"') {
      quoted = !quoted;
    } else if (ch === "," && !quoted) {
      row.push(field);
      field = "";
    } else if ((ch === "\n" || ch === "\r") && !quoted) {
      if (ch === "\r" && next === "\n") i += 1;
      row.push(field);
      if (row.some((v) => String(v).trim() !== "")) rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  row.push(field);
  if (row.some((v) => String(v).trim() !== "")) rows.push(row);
  if (!rows.length) return [];

  const headers = rows[0].map((h) => String(h).trim().toLowerCase());
  return rows.slice(1).map((values, index) => {
    const out = { __row: index + 2 };
    headers.forEach((header, i) => {
      out[header] = values[i] ?? "";
    });
    return out;
  });
};

const csvEscape = (value) => {
  const string = String(value ?? "");
  if (/[",\n\r]/.test(string)) return `"${string.replace(/"/g, '""')}"`;
  return string;
};

const CSV_HEADERS = [
  "identifier",
  "type",
  "label",
  "description",
  "status",
  "source",
  "tag",
  "connected-item",
  "connected-type",
  "connection-type",
  "connection-start",
  "connection-end",
  "directed",
  "connection-notes",
  "evidence",
  "main-target",
];

const downloadText = (filename, content, mime = "text/csv;charset=utf-8") => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const buildGangSample = () => {
  const n = {};
  const node = (key, identifier, type, mainTarget = false, extras = {}) => {
    n[key] = makeNode({ identifier, type, mainTarget, ...extras });
    return n[key];
  };
  node("target", "Marcus Vale", "Person", true, { label: "Primary Subject", status: "Active", tag: "Gang Target" });
  node("acct", "@marcus.v", "Social Account", false, { source: "Instagram", tag: "Public Account" });
  node("a1", "Darius Cole", "Person", false, { tag: "Associate" });
  node("a2", "Nico Perez", "Person", false, { tag: "Associate" });
  node("a3", "Trey Bennett", "Person", false, { tag: "Associate" });
  node("house", "418 Harbor Street", "Residence", false, { status: "Observed Location" });
  node("club", "Southside Social Club", "Location", false, { status: "Observed Location" });
  node("car", "Black Charger - VA SAMPLE", "Vehicle", false, { tag: "Observed Vehicle" });
  node("acct2", "@dcole.sample", "Social Account", false, { source: "Instagram" });
  node("acct3", "@nperez.sample", "Social Account", false, { source: "Instagram" });

  const edge = (from, to, connectionType, start = "", end = "", notes = "", directed = false) =>
    makeEdge({ from: n[from].id, to: n[to].id, connectionType, start, end, notes, directed });

  return {
    nodes: Object.values(n),
    edges: [
      edge("target", "acct", "Uses", "2026-06-01T00:00", "", "Fictional sample account attribution.", true),
      edge("acct", "acct2", "Follows", "2026-06-03T10:00", "", "Mutual public social-media connection.", true),
      edge("acct", "acct3", "Follows", "2026-06-05T09:30", "", "Mutual public social-media connection.", true),
      edge("target", "a1", "Associate", "2026-06-10T18:00", "", "Fictional investigative association."),
      edge("target", "a2", "Associate", "2026-06-12T19:00", "", "Fictional investigative association."),
      edge("target", "a3", "Associate", "2026-06-15T20:00", "2026-08-01T00:00", "Historical fictional association."),
      edge("a1", "acct2", "Uses", "2026-06-01T00:00", "", "Fictional sample account attribution.", true),
      edge("a2", "acct3", "Uses", "2026-06-01T00:00", "", "Fictional sample account attribution.", true),
      edge("target", "house", "Visited", "2026-08-28T21:10", "2026-08-29T00:25", "Observed arrival/departure for sample timeline.", true),
      edge("a1", "house", "Visited", "2026-08-28T21:42", "2026-08-28T23:55", "Overlaps primary subject for sample analysis.", true),
      edge("a2", "house", "Visited", "2026-08-28T22:06", "2026-08-29T00:40", "Overlaps primary subject for sample analysis.", true),
      edge("target", "club", "Visited", "2026-08-30T19:00", "2026-08-30T21:20", "Fictional sample observation.", true),
      edge("a3", "club", "Visited", "2026-08-30T19:35", "2026-08-30T20:10", "Fictional sample observation.", true),
      edge("target", "car", "Uses", "2026-08-20T00:00", "", "Fictional sample vehicle association.", true),
    ],
  };
};

const buildEPSample = () => {
  const n = {};
  const node = (key, identifier, type, mainTarget = false, extras = {}) => {
    n[key] = makeNode({ identifier, type, mainTarget, ...extras });
    return n[key];
  };
  node("principal", "Elena Ward", "Person", true, { label: "Principal", status: "Protected", tag: "Principal" });
  node("spouse", "Daniel Ward", "Person", false, { label: "Spouse", status: "Authorized" });
  node("ea", "Maya Chen", "Person", false, { label: "Executive Assistant", status: "Authorized" });
  node("driver", "Owen Brooks", "Person", false, { label: "Protective Driver", status: "Authorized" });
  node("vendor", "Sam Ortega", "Person", false, { label: "Event Vendor", status: "Conditional Access" });
  node("unknown", "Unknown Contact 01", "Person", false, { status: "Not Authorized", tag: "Review" });
  node("res", "Ward Residence", "Residence", false, { status: "Protected Location" });
  node("office", "Northbridge HQ", "Organization", false, { status: "Routine" });
  node("venue", "Capitol View Forum", "Event", false, { status: "Upcoming" });
  node("vehicle", "Principal SUV 01", "Vehicle", false, { status: "Assigned" });

  const edge = (from, to, type, start = "", end = "", notes = "", directed = false) =>
    makeEdge({ from: n[from].id, to: n[to].id, connectionType: type, start, end, notes, directed });

  return {
    nodes: Object.values(n),
    edges: [
      edge("principal", "spouse", "Family"),
      edge("principal", "ea", "Works With"),
      edge("principal", "driver", "Authorized"),
      edge("principal", "res", "Resides At", "2026-01-01T00:00", "", "Fictional protected residence.", true),
      edge("principal", "office", "Works At", "2026-01-01T00:00", "", "Fictional corporate office.", true),
      edge("driver", "vehicle", "Uses", "2026-08-01T00:00", "", "Assigned protective vehicle.", true),
      edge("principal", "venue", "Attended", "2026-09-03T14:00", "2026-09-03T16:30", "Fictional event movement.", true),
      edge("ea", "venue", "Attended", "2026-09-03T13:20", "2026-09-03T17:00", "Advance/support presence.", true),
      edge("vendor", "venue", "Authorized", "2026-09-03T12:30", "2026-09-03T15:30", "Vendor access window.", true),
      edge("unknown", "venue", "Unauthorized", "2026-09-03T14:20", "2026-09-03T14:42", "Fictional contact requiring review.", true),
      edge("unknown", "principal", "Observed With", "2026-09-03T14:28", "2026-09-03T14:31", "Brief fictional proximity event."),
    ],
  };
};

const connectedDegreeSet = (focusId, edges, degree) => {
  if (!focusId || degree === "all") return null;
  const maxDepth = Number(degree);
  const seen = new Set([focusId]);
  let frontier = new Set([focusId]);
  for (let d = 0; d < maxDepth; d += 1) {
    const next = new Set();
    edges.forEach((edge) => {
      if (frontier.has(edge.from) && !seen.has(edge.to)) next.add(edge.to);
      if (frontier.has(edge.to) && !seen.has(edge.from)) next.add(edge.from);
    });
    next.forEach((id) => seen.add(id));
    frontier = next;
  }
  return seen;
};

const LinkAnalysis = () => {
  const [data, setData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : blankState();
    } catch {
      return blankState();
    }
  });
  const [graphType, setGraphType] = useState("network");
  const [physics, setPhysics] = useState(true);
  const [viewMode, setViewMode] = useState("all");
  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [focusNodeId, setFocusNodeId] = useState("");
  const [focusDegree, setFocusDegree] = useState("all");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [connectionFilter, setConnectionFilter] = useState("All");
  const [targetOnly, setTargetOnly] = useState(false);
  const [activityFilter, setActivityFilter] = useState("All");
  const [modal, setModal] = useState(null);
  const [importStatus, setImportStatus] = useState("");
  const [timelineValue, setTimelineValue] = useState(0);
  const [timelineGranularity, setTimelineGranularity] = useState("Auto");
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [zoom, setZoom] = useState(0.78);
  const [pan, setPan] = useState({ x: 20, y: 20 });
  const [draggingNode, setDraggingNode] = useState(null);
  const [panning, setPanning] = useState(null);
  const fileRef = useRef(null);
  const stageRef = useRef(null);
  const dataRef = useRef(data);

  useEffect(() => {
    dataRef.current = data;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Unable to save Link Analysis session:", error);
    }
  }, [data]);

  const timelineBounds = useMemo(() => {
    const starts = data.edges.map((e) => Date.parse(e.start)).filter(Number.isFinite);
    const ends = data.edges.map((e) => Date.parse(e.end)).filter(Number.isFinite);
    const all = [...starts, ...ends];
    if (!all.length) {
      const now = Date.now();
      return { min: now, max: now + 60 * 60 * 1000 };
    }
    const min = Math.min(...all);
    const max = Math.max(...all, min + 60 * 1000);
    return { min, max };
  }, [data.edges]);

  useEffect(() => {
    if (!timelineValue || timelineValue < timelineBounds.min || timelineValue > timelineBounds.max) {
      setTimelineValue(timelineBounds.min);
    }
  }, [timelineBounds.min, timelineBounds.max]);

  const resolvedGranularity = useMemo(() => {
    if (timelineGranularity !== "Auto") return timelineGranularity;
    const duration = timelineBounds.max - timelineBounds.min;
    if (duration <= 2 * 24 * 60 * 60 * 1000) return "Hour";
    if (duration <= 60 * 24 * 60 * 60 * 1000) return "Day";
    if (duration <= 365 * 24 * 60 * 60 * 1000) return "Week";
    return "Month";
  }, [timelineGranularity, timelineBounds]);

  const stepMs = useMemo(() => {
    if (resolvedGranularity === "Hour") return 60 * 60 * 1000;
    if (resolvedGranularity === "Day") return 24 * 60 * 60 * 1000;
    if (resolvedGranularity === "Week") return 7 * 24 * 60 * 60 * 1000;
    return 30 * 24 * 60 * 60 * 1000;
  }, [resolvedGranularity]);

  useEffect(() => {
    if (!playing || viewMode !== "timeline") return undefined;
    const timer = window.setInterval(() => {
      setTimelineValue((current) => {
        const next = current + stepMs;
        if (next >= timelineBounds.max) {
          setPlaying(false);
          return timelineBounds.max;
        }
        return next;
      });
    }, Math.max(120, 900 / playbackSpeed));
    return () => window.clearInterval(timer);
  }, [playing, playbackSpeed, stepMs, timelineBounds.max, viewMode]);

  const edgeActiveAt = (edge, stamp) => {
    if (viewMode !== "timeline") return true;
    const start = edge.start ? Date.parse(edge.start) : -Infinity;
    const end = edge.end ? Date.parse(edge.end) : Infinity;
    return (!Number.isFinite(start) || start <= stamp) && (!Number.isFinite(end) || end >= stamp);
  };

  const filteredEdgesBase = useMemo(() => {
    return data.edges.filter((edge) => edgeActiveAt(edge, timelineValue));
  }, [data.edges, viewMode, timelineValue]);

  const focusSet = useMemo(
    () => connectedDegreeSet(focusNodeId, filteredEdgesBase, focusDegree),
    [focusNodeId, filteredEdgesBase, focusDegree]
  );

  const visibleData = useMemo(() => {
    const query = normalizeName(search);
    let nodeIds = new Set(
      data.nodes
        .filter((node) => !node.hidden)
        .filter((node) => typeFilter === "All" || node.type === typeFilter)
        .filter((node) => !targetOnly || node.mainTarget)
        .filter((node) => {
          if (!query) return true;
          return [node.identifier, node.label, node.description, node.status, node.source, node.tag]
            .some((value) => normalizeName(value).includes(query));
        })
        .filter((node) => !focusSet || focusSet.has(node.id))
        .map((node) => node.id)
    );

    let edges = filteredEdgesBase.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
    if (connectionFilter !== "All") edges = edges.filter((edge) => edge.connectionType === connectionFilter);
    if (activityFilter === "Active") edges = edges.filter((edge) => !edge.end || Date.parse(edge.end) >= Date.now());
    if (activityFilter === "Historical") edges = edges.filter((edge) => edge.end && Date.parse(edge.end) < Date.now());

    if (viewMode === "timeline") {
      const activeNodeIds = new Set();
      edges.forEach((edge) => {
        activeNodeIds.add(edge.from);
        activeNodeIds.add(edge.to);
      });
      data.nodes.filter((n) => n.mainTarget && !n.hidden).forEach((n) => activeNodeIds.add(n.id));
      nodeIds = new Set([...nodeIds].filter((id) => activeNodeIds.has(id)));
      edges = edges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
    }

    const nodes = data.nodes.filter((node) => nodeIds.has(node.id));
    return { nodes, edges };
  }, [data, search, typeFilter, targetOnly, focusSet, filteredEdgesBase, connectionFilter, activityFilter, viewMode]);

  const selectedNode = data.nodes.find((node) => node.id === selectedNodeId) || null;

  const relationshipsForSelected = useMemo(() => {
    if (!selectedNode) return [];
    return data.edges
      .filter((edge) => edge.from === selectedNode.id || edge.to === selectedNode.id)
      .map((edge) => {
        const otherId = edge.from === selectedNode.id ? edge.to : edge.from;
        return { edge, other: data.nodes.find((n) => n.id === otherId) };
      });
  }, [selectedNode, data]);

  const overlaps = useMemo(() => {
    const people = new Set(data.nodes.filter((n) => n.type === "Person").map((n) => n.id));
    const placeTypes = new Set(["Residence", "Location", "Organization", "Event"]);
    const places = data.nodes.filter((n) => placeTypes.has(n.type));
    const results = [];

    places.forEach((place) => {
      const visits = data.edges
        .filter((e) => (e.from === place.id && people.has(e.to)) || (e.to === place.id && people.has(e.from)))
        .filter((e) => e.start)
        .map((e) => ({
          edge: e,
          personId: people.has(e.from) ? e.from : e.to,
          start: Date.parse(e.start),
          end: e.end ? Date.parse(e.end) : Infinity,
        }))
        .filter((v) => Number.isFinite(v.start));

      for (let i = 0; i < visits.length; i += 1) {
        for (let j = i + 1; j < visits.length; j += 1) {
          const a = visits[i];
          const b = visits[j];
          const start = Math.max(a.start, b.start);
          const end = Math.min(a.end, b.end);
          if (start < end && Number.isFinite(end)) {
            results.push({
              id: `${place.id}-${a.edge.id}-${b.edge.id}`,
              place,
              a: data.nodes.find((n) => n.id === a.personId),
              b: data.nodes.find((n) => n.id === b.personId),
              start,
              end,
            });
          }
        }
      }
    });
    return results.sort((a, b) => a.start - b.start);
  }, [data]);

  const updateNode = (id, patch) => {
    setData((current) => ({ ...current, nodes: current.nodes.map((node) => node.id === id ? { ...node, ...patch } : node) }));
  };

  const upsertNodeByIdentifier = (state, input) => {
    const key = normalizeName(input.identifier);
    let node = state.nodes.find((n) => normalizeName(n.identifier) === key);
    if (node) {
      const patch = {};
      ["type", "label", "description", "status", "source", "tag"].forEach((field) => {
        if (input[field]) patch[field] = input[field];
      });
      if (input.mainTarget) patch.mainTarget = true;
      node = { ...node, ...patch };
      state.nodes = state.nodes.map((n) => n.id === node.id ? node : n);
    } else {
      node = makeNode(input);
      state.nodes.push(node);
    }
    return node;
  };

  const importCSV = async (file) => {
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    let imported = 0;
    let skipped = 0;
    let merged = 0;
    const problems = [];

    setData((current) => {
      const next = { nodes: current.nodes.map((n) => ({ ...n })), edges: current.edges.map((e) => ({ ...e })) };
      rows.forEach((row) => {
        const identifier = String(row.identifier || "").trim();
        if (!identifier) {
          skipped += 1;
          problems.push(`Row ${row.__row}: missing identifier`);
          return;
        }
        const existing = next.nodes.find((n) => normalizeName(n.identifier) === normalizeName(identifier));
        if (existing) merged += 1;
        if (existing && row.type && normalizeName(existing.type) !== normalizeName(row.type)) {
          problems.push(`Row ${row.__row}: type conflict for ${identifier}; kept ${existing.type}`);
        }
        const node = upsertNodeByIdentifier(next, {
          identifier,
          type: existing?.type || row.type || "Other",
          label: row.label || "",
          description: row.description || "",
          status: row.status || "",
          source: row.source || "",
          tag: row.tag || "",
          mainTarget: boolish(row["main-target"]),
        });

        const connectedIdentifier = String(row["connected-item"] || "").trim();
        if (connectedIdentifier) {
          const connected = upsertNodeByIdentifier(next, {
            identifier: connectedIdentifier,
            type: row["connected-type"] || "Other",
          });
          if (node.id !== connected.id) {
            const candidate = {
              from: node.id,
              to: connected.id,
              connectionType: row["connection-type"] || "Associate",
              start: row["connection-start"] || "",
              end: row["connection-end"] || "",
              directed: boolish(row.directed),
              notes: row["connection-notes"] || "",
              evidence: row.evidence || "",
            };
            const duplicate = next.edges.some((e) =>
              e.from === candidate.from && e.to === candidate.to && e.connectionType === candidate.connectionType &&
              e.start === candidate.start && e.end === candidate.end
            );
            if (!duplicate) next.edges.push(makeEdge(candidate));
          }
        }
        imported += 1;
      });
      return next;
    });

    setImportStatus(`${imported} rows imported${merged ? `, ${merged} merged with existing nodes` : ""}${skipped ? `, ${skipped} skipped` : ""}.${problems.length ? ` ${problems.slice(0, 2).join("; ")}` : ""}`);
    if (fileRef.current) fileRef.current.value = "";
  };

  const saveNodeFromModal = (form) => {
    if (!String(form.identifier || "").trim()) return;
    setData((current) => {
      const next = { nodes: current.nodes.map((n) => ({ ...n })), edges: [...current.edges] };
      let node;

      // Only treat this as an edit when the supplied id actually belongs to
      // a node already in state. New modal forms must create a node.
      const existingById = form.id
        ? next.nodes.find((n) => n.id === form.id)
        : null;

      if (existingById) {
        node = { ...existingById, ...form, id: existingById.id };
        next.nodes = next.nodes.map((n) => n.id === existingById.id ? node : n);
      } else {
        const existing = next.nodes.find((n) => normalizeName(n.identifier) === normalizeName(form.identifier));
        if (existing) {
          node = { ...existing, ...form, id: existing.id };
          next.nodes = next.nodes.map((n) => n.id === existing.id ? node : n);
        } else {
          // Strip modal-only / accidental ids before constructing the real node.
          const { id: _discardedId, connectTo, connectionType, connectionStart, connectionEnd, directed, connectionNotes, evidence, ...nodeFields } = form;
          node = makeNode(nodeFields);
          next.nodes.push(node);
        }
      }
      if (form.connectTo && form.connectTo !== node.id) {
        next.edges.push(makeEdge({
          from: node.id,
          to: form.connectTo,
          connectionType: form.connectionType || "Associate",
          start: form.connectionStart || "",
          end: form.connectionEnd || "",
          directed: Boolean(form.directed),
          notes: form.connectionNotes || "",
          evidence: form.evidence || "",
        }));
      }
      return next;
    });
    setModal(null);
  };

  const saveConnectionFromModal = (form) => {
    if (!form.from || !form.to || form.from === form.to) return;
    setData((current) => {
      const existingById = form.id
        ? current.edges.find((edge) => edge.id === form.id)
        : null;

      if (existingById) {
        return {
          ...current,
          edges: current.edges.map((edge) =>
            edge.id === existingById.id
              ? { ...existingById, ...form, id: existingById.id }
              : edge
          ),
        };
      }

      const { id: _discardedId, ...edgeFields } = form;
      return {
        ...current,
        edges: [...current.edges, makeEdge(edgeFields)],
      };
    });
    setModal(null);
  };

  const exportCSV = (scope = "all") => {
    const sourceNodes = scope === "visible" ? visibleData.nodes : data.nodes;
    const sourceEdges = scope === "visible" ? visibleData.edges : data.edges;
    const nodeMap = new Map(sourceNodes.map((n) => [n.id, n]));
    const rows = [];
    sourceNodes.forEach((node) => {
      const attached = sourceEdges.filter((edge) => edge.from === node.id);
      if (!attached.length) {
        rows.push([node.identifier, node.type, node.label, node.description, node.status, node.source, node.tag, "", "", "", "", "", "", "", "", node.mainTarget]);
      } else {
        attached.forEach((edge) => {
          const connected = nodeMap.get(edge.to) || data.nodes.find((n) => n.id === edge.to);
          rows.push([
            node.identifier, node.type, node.label, node.description, node.status, node.source, node.tag,
            connected?.identifier || "", connected?.type || "", edge.connectionType, edge.start, edge.end,
            edge.directed, edge.notes, edge.evidence, node.mainTarget,
          ]);
        });
      }
    });
    const csv = [CSV_HEADERS, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
    downloadText(`weathervane-link-analysis-${scope}.csv`, csv);
  };

  const downloadTemplate = () => {
    const example = [
      CSV_HEADERS,
      ["Primary Subject", "Person", "", "", "Active", "", "Target", "123 Main Street", "Residence", "Visited", "2026-09-03T14:00", "2026-09-03T16:00", "true", "Optional connection notes", "Optional source/evidence note", "true"],
    ];
    downloadText("weathervane-link-analysis-template.csv", example.map((r) => r.map(csvEscape).join(",")).join("\n"));
  };

  const loadSample = (kind) => {
    const sample = kind === "gang" ? buildGangSample() : buildEPSample();
    setData(sample);
    setSelectedNodeId("");
    setFocusNodeId("");
    setFocusDegree("all");
    setViewMode("all");
    setImportStatus(`${kind === "gang" ? "Investigative" : "Executive protection"} fictional sample loaded.`);
    requestAnimationFrame(() => fitGraph());
  };

  const clearWorkspace = () => {
    if (!window.confirm("Clear all Link Analysis nodes and connections from this browser session?")) return;
    setData(blankState());
    setSelectedNodeId("");
    setFocusNodeId("");
    setImportStatus("Workspace cleared.");
    sessionStorage.removeItem(STORAGE_KEY);
  };

  const fitGraph = () => {
    const nodes = visibleData.nodes.length ? visibleData.nodes : data.nodes;
    if (!nodes.length) {
      setZoom(0.78);
      setPan({ x: 20, y: 20 });
      return;
    }
    const width = stageRef.current?.clientWidth || 1000;
    const height = stageRef.current?.clientHeight || 650;
    const minX = Math.min(...nodes.map((n) => Number(n.x) || GRAPH_WIDTH / 2));
    const maxX = Math.max(...nodes.map((n) => Number(n.x) || GRAPH_WIDTH / 2));
    const minY = Math.min(...nodes.map((n) => Number(n.y) || GRAPH_HEIGHT / 2));
    const maxY = Math.max(...nodes.map((n) => Number(n.y) || GRAPH_HEIGHT / 2));
    const graphW = Math.max(420, maxX - minX + 260);
    const graphH = Math.max(320, maxY - minY + 220);
    const nextZoom = clamp(Math.min(width / graphW, height / graphH), 0.35, 1.25);
    setZoom(nextZoom);
    setPan({
      x: width / 2 - ((minX + maxX) / 2) * nextZoom,
      y: height / 2 - ((minY + maxY) / 2) * nextZoom,
    });
  };

  const visualPositions = useMemo(() => {
    if (graphType !== "visual") return null;
    const nodes = visibleData.nodes;
    const edges = visibleData.edges;
    const positions = {};
    const targets = nodes.filter((n) => n.mainTarget);
    const anchors = targets.length ? targets : nodes.slice(0, 1);
    const centerY = GRAPH_HEIGHT / 2;
    anchors.forEach((target, index) => {
      const spacing = Math.min(440, GRAPH_WIDTH / Math.max(anchors.length, 1));
      positions[target.id] = {
        x: GRAPH_WIDTH / 2 + (index - (anchors.length - 1) / 2) * spacing,
        y: centerY,
      };
    });

    const placed = new Set(Object.keys(positions));
    const typeOrder = NODE_TYPES;
    anchors.forEach((anchor, anchorIndex) => {
      const relatedIds = new Set();
      edges.forEach((edge) => {
        if (edge.from === anchor.id) relatedIds.add(edge.to);
        if (edge.to === anchor.id) relatedIds.add(edge.from);
      });
      const related = nodes.filter((n) => relatedIds.has(n.id) && !placed.has(n.id));
      const groups = typeOrder.map((type) => ({ type, nodes: related.filter((n) => n.type === type) })).filter((g) => g.nodes.length);
      const anchorPos = positions[anchor.id];
      let slot = 0;
      const total = related.length || 1;
      groups.forEach((group, groupIndex) => {
        group.nodes.forEach((node) => {
          if (node.visualPinned) {
            positions[node.id] = {
              x: clamp(Number(node.x) || GRAPH_WIDTH / 2, 130, GRAPH_WIDTH - 130),
              y: clamp(Number(node.y) || GRAPH_HEIGHT / 2, 90, GRAPH_HEIGHT - 90),
            };
          } else {
            const angle = (Math.PI * 2 * slot) / total - Math.PI / 2 + anchorIndex * 0.25;
            const ring = 280 + groupIndex * 42;
            positions[node.id] = {
              x: clamp(anchorPos.x + Math.cos(angle) * ring, 130, GRAPH_WIDTH - 130),
              y: clamp(anchorPos.y + Math.sin(angle) * ring, 90, GRAPH_HEIGHT - 90),
            };
          }
          placed.add(node.id);
          slot += 1;
        });
      });
    });

    const unplaced = nodes.filter((n) => !placed.has(n.id));
    unplaced.forEach((node, index) => {
      const cols = Math.max(1, Math.ceil(Math.sqrt(unplaced.length)));
      positions[node.id] = node.visualPinned
        ? { x: clamp(Number(node.x) || GRAPH_WIDTH / 2, 130, GRAPH_WIDTH - 130), y: clamp(Number(node.y) || GRAPH_HEIGHT / 2, 90, GRAPH_HEIGHT - 90) }
        : { x: 180 + (index % cols) * 240, y: 120 + Math.floor(index / cols) * 160 };
    });
    return positions;
  }, [graphType, visibleData]);

  const positionedNodes = useMemo(() => visibleData.nodes.map((node) => ({
    ...node,
    px: visualPositions?.[node.id]?.x ?? Number(node.x) ?? GRAPH_WIDTH / 2,
    py: visualPositions?.[node.id]?.y ?? Number(node.y) ?? GRAPH_HEIGHT / 2,
  })), [visibleData.nodes, visualPositions]);

  const positionedMap = useMemo(() => new Map(positionedNodes.map((n) => [n.id, n])), [positionedNodes]);

  useEffect(() => {
    if (!physics || graphType !== "network" || draggingNode || visibleData.nodes.length < 2) return undefined;
    let frame;
    let last = performance.now();
    const tick = (time) => {
      if (time - last > 32) {
        last = time;
        setData((current) => {
          const visibleIds = new Set(visibleData.nodes.map((n) => n.id));
          const positions = new Map(current.nodes.map((n) => [n.id, { x: Number(n.x) || GRAPH_WIDTH / 2, y: Number(n.y) || GRAPH_HEIGHT / 2 }]));
          const delta = new Map();
          visibleData.edges.forEach((edge) => {
            if (!visibleIds.has(edge.from) || !visibleIds.has(edge.to)) return;
            const a = positions.get(edge.from);
            const b = positions.get(edge.to);
            if (!a || !b) return;
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.max(1, Math.hypot(dx, dy));
            let force = 0;
            if (dist > MAX_EDGE_LENGTH) force = Math.min(5.5, (dist - MAX_EDGE_LENGTH) * 0.025);
            if (dist < MIN_EDGE_LENGTH) force = -Math.min(5.5, (MIN_EDGE_LENGTH - dist) * 0.035);
            if (!force) return;
            const ux = dx / dist;
            const uy = dy / dist;
            const da = delta.get(edge.from) || { x: 0, y: 0 };
            const db = delta.get(edge.to) || { x: 0, y: 0 };
            da.x += ux * force;
            da.y += uy * force;
            db.x -= ux * force;
            db.y -= uy * force;
            delta.set(edge.from, da);
            delta.set(edge.to, db);
          });

          const visibleNodes = current.nodes.filter((n) => visibleIds.has(n.id));
          for (let i = 0; i < visibleNodes.length; i += 1) {
            for (let j = i + 1; j < visibleNodes.length; j += 1) {
              const a = positions.get(visibleNodes[i].id);
              const b = positions.get(visibleNodes[j].id);
              const dx = b.x - a.x;
              const dy = b.y - a.y;
              const dist = Math.max(1, Math.hypot(dx, dy));
              const collision = graphType === "network" ? 78 : 160;
              if (dist >= collision) continue;
              const push = Math.min(3.5, (collision - dist) * 0.04);
              const ux = dx / dist;
              const uy = dy / dist;
              const da = delta.get(visibleNodes[i].id) || { x: 0, y: 0 };
              const db = delta.get(visibleNodes[j].id) || { x: 0, y: 0 };
              da.x -= ux * push;
              da.y -= uy * push;
              db.x += ux * push;
              db.y += uy * push;
              delta.set(visibleNodes[i].id, da);
              delta.set(visibleNodes[j].id, db);
            }
          }

          if (!delta.size) return current;
          return {
            ...current,
            nodes: current.nodes.map((node) => {
              const d = delta.get(node.id);
              if (!d) return node;
              return {
                ...node,
                x: clamp((Number(node.x) || GRAPH_WIDTH / 2) + d.x, 70, GRAPH_WIDTH - 70),
                y: clamp((Number(node.y) || GRAPH_HEIGHT / 2) + d.y, 70, GRAPH_HEIGHT - 70),
              };
            }),
          };
        });
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [physics, graphType, draggingNode, visibleData.nodes, visibleData.edges]);

  const stagePoint = (clientX, clientY) => {
    const rect = stageRef.current.getBoundingClientRect();
    return {
      x: (clientX - rect.left - pan.x) / zoom,
      y: (clientY - rect.top - pan.y) / zoom,
    };
  };

  const onNodePointerDown = (event, node) => {
    if (graphType === "visual" && node.mainTarget) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = stagePoint(event.clientX, event.clientY);
    setDraggingNode({ id: node.id, visual: graphType === "visual", offsetX: point.x - node.px, offsetY: point.y - node.py });
  };

  const onStagePointerMove = (event) => {
    if (draggingNode) {
      const point = stagePoint(event.clientX, event.clientY);
      updateNode(draggingNode.id, {
        x: clamp(point.x - draggingNode.offsetX, 50, GRAPH_WIDTH - 50),
        y: clamp(point.y - draggingNode.offsetY, 50, GRAPH_HEIGHT - 50),
        ...(draggingNode.visual ? { visualPinned: true } : {}),
      });
    } else if (panning) {
      setPan({ x: event.clientX - panning.startX + panning.panX, y: event.clientY - panning.startY + panning.panY });
    }
  };

  const onStagePointerDown = (event) => {
    if (event.target.closest?.(".la-node")) return;
    setPanning({ startX: event.clientX, startY: event.clientY, panX: pan.x, panY: pan.y });
  };

  const onStagePointerUp = () => {
    setDraggingNode(null);
    setPanning(null);
  };

  const onWheel = (event) => {
    event.preventDefault();
    const rect = stageRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const worldX = (mouseX - pan.x) / zoom;
    const worldY = (mouseY - pan.y) / zoom;
    const nextZoom = clamp(zoom * (event.deltaY > 0 ? 0.9 : 1.1), 0.25, 2.2);
    setZoom(nextZoom);
    setPan({ x: mouseX - worldX * nextZoom, y: mouseY - worldY * nextZoom });
  };

  const exportPNG = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1800;
    canvas.height = 1050;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#07101d";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#c6aa6b";
    ctx.font = "700 18px Arial";
    ctx.fillText("WEATHERVANE INTEL • LINK ANALYSIS", 60, 48);
    ctx.fillStyle = "#f4f7fb";
    ctx.font = "700 34px Arial";
    ctx.fillText("Network Graph", 60, 92);
    ctx.fillStyle = "rgba(244,247,251,.62)";
    ctx.font = "16px Arial";
    ctx.fillText(viewMode === "timeline" ? `Timeline: ${formatDateTime(timelineValue)}` : "See All", 60, 122);

    const nodes = positionedNodes;
    if (!nodes.length) {
      ctx.fillStyle = "#f4f7fb";
      ctx.fillText("No visible nodes to export.", 60, 180);
    } else {
      const minX = Math.min(...nodes.map((n) => n.px));
      const maxX = Math.max(...nodes.map((n) => n.px));
      const minY = Math.min(...nodes.map((n) => n.py));
      const maxY = Math.max(...nodes.map((n) => n.py));
      const area = { x: 60, y: 150, w: 1680, h: 820 };
      const scale = Math.min(area.w / Math.max(420, maxX - minX + 220), area.h / Math.max(320, maxY - minY + 180));
      const ox = area.x + area.w / 2 - ((minX + maxX) / 2) * scale;
      const oy = area.y + area.h / 2 - ((minY + maxY) / 2) * scale;
      const toCanvas = (x, y) => ({ x: ox + x * scale, y: oy + y * scale });

      ctx.lineWidth = 2;
      visibleData.edges.forEach((edge) => {
        const a = positionedMap.get(edge.from);
        const b = positionedMap.get(edge.to);
        if (!a || !b) return;
        const p1 = toCanvas(a.px, a.py);
        const p2 = toCanvas(b.px, b.py);
        ctx.strokeStyle = edge.connectionType === "Unauthorized" ? "rgba(233,130,130,.8)" : "rgba(139,188,204,.58)";
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      });

      nodes.forEach((node) => {
        const p = toCanvas(node.px, node.py);
        if (graphType === "visual") {
          const w = 190;
          const h = 68;
          ctx.fillStyle = node.mainTarget ? "#142334" : "#0d1827";
          ctx.strokeStyle = node.mainTarget ? "#c6aa6b" : "rgba(139,188,204,.52)";
          ctx.lineWidth = node.mainTarget ? 3 : 1.5;
          ctx.beginPath();
          ctx.roundRect(p.x - w / 2, p.y - h / 2, w, h, 12);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#f4f7fb";
          ctx.font = "700 14px Arial";
          ctx.fillText(node.identifier.slice(0, 24), p.x - 80, p.y - 4);
          ctx.fillStyle = "rgba(244,247,251,.62)";
          ctx.font = "11px Arial";
          ctx.fillText(node.type.toUpperCase(), p.x - 80, p.y + 16);
        } else {
          ctx.fillStyle = node.mainTarget ? "#c6aa6b" : "#8bbccc";
          ctx.strokeStyle = "#07101d";
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(p.x, p.y, node.mainTarget ? 22 : 17, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = "#f4f7fb";
          ctx.font = "700 12px Arial";
          ctx.fillText(node.identifier.slice(0, 26), p.x + 26, p.y + 4);
        }
      });
    }

    ctx.fillStyle = "rgba(244,247,251,.48)";
    ctx.font = "12px Arial";
    ctx.fillText("Generated locally in the browser by Weathervane Intel Link Analysis", 60, 1020);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "weathervane-link-analysis.png";
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const nudgeTimeline = (direction) => {
    setTimelineValue((current) => clamp(current + direction * stepMs, timelineBounds.min, timelineBounds.max));
  };

  const activeConnectionsForNode = (nodeId) => visibleData.edges.filter((e) => e.from === nodeId || e.to === nodeId).length;

  return (
    <main className="link-analysis-page">
      <div className="la-contour-background" aria-hidden="true" />
      <div className="link-analysis-shell">
        <section className="la-hero">
          <div className="la-hero-copy">
            <span className="la-eyebrow">Weathervane Intel • Local Analysis Workspace</span>
            <h1>Link Analysis</h1>
            <p>
              Build relationship and physical-network graphs for investigations, protective operations, and operational planning without transmitting graph data to a Weathervane Intel application API.
            </p>
            <div className="la-local-notice">
              <LockOutlinedIcon aria-hidden="true" />
              <span><strong>Browser-local workspace.</strong> Graph data is retained only in this browser session unless you explicitly export it.</span>
            </div>
          </div>
          <div className="la-hero-stats">
            <div><span>Nodes</span><strong>{data.nodes.length}</strong></div>
            <div><span>Connections</span><strong>{data.edges.length}</strong></div>
            <div><span>Main targets</span><strong>{data.nodes.filter((n) => n.mainTarget).length}</strong></div>
            <div><span>Overlap findings</span><strong>{overlaps.length}</strong></div>
          </div>
        </section>

        <section className="la-actions-panel">
          <div className="la-actions-primary">
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => importCSV(e.target.files?.[0])} />
            <button type="button" className="la-button la-button-primary" onClick={() => fileRef.current?.click()}><UploadFileOutlinedIcon /> Upload CSV</button>
            <button type="button" className="la-button" onClick={downloadTemplate}><DownloadOutlinedIcon /> CSV Template</button>
            <button type="button" className="la-button" onClick={() => setModal({ type: "node", value: null })}><AddOutlinedIcon /> Add Item</button>
            <button type="button" className="la-button" onClick={() => setModal({ type: "edge", value: null })}><LinkOutlinedIcon /> Add Connection</button>
          </div>
          <div className="la-actions-secondary">
            <button type="button" className="la-button la-button-quiet" onClick={() => loadSample("gang")}><GroupsOutlinedIcon /> Sample Investigative Graph</button>
            <button type="button" className="la-button la-button-quiet" onClick={() => loadSample("ep")}><ShieldOutlinedIcon /> Sample EP Graph</button>
            <button type="button" className="la-button la-button-danger" onClick={clearWorkspace}><RestartAltOutlinedIcon /> Clear Workspace</button>
          </div>
          {importStatus && <div className="la-import-status">{importStatus}</div>}
        </section>

        <section className="la-workspace">
          <aside className="la-sidebar">
            <div className="la-sidebar-card">
              <div className="la-sidebar-heading"><FilterAltOutlinedIcon /><div><small>Graph controls</small><strong>Filter & Focus</strong></div></div>
              <label className="la-search"><SearchOutlinedIcon /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search nodes" /></label>
              <label className="la-control-field"><span>Node type</span><select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option>All</option>{NODE_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
              <label className="la-control-field"><span>Connection type</span><select value={connectionFilter} onChange={(e) => setConnectionFilter(e.target.value)}><option>All</option>{CONNECTION_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
              <label className="la-control-field"><span>Connection status</span><select value={activityFilter} onChange={(e) => setActivityFilter(e.target.value)}><option>All</option><option>Active</option><option>Historical</option></select></label>
              <label className="la-check"><input type="checkbox" checked={targetOnly} onChange={(e) => setTargetOnly(e.target.checked)} /> Main targets only</label>
              <div className="la-divider" />
              <label className="la-control-field"><span>Focus node</span><select value={focusNodeId} onChange={(e) => setFocusNodeId(e.target.value)}><option value="">No focus</option>{data.nodes.map((n) => <option value={n.id} key={n.id}>{n.identifier}</option>)}</select></label>
              <label className="la-control-field"><span>Degrees of separation</span><select value={focusDegree} onChange={(e) => setFocusDegree(e.target.value)} disabled={!focusNodeId}><option value="1">First degree</option><option value="2">First + second degree</option><option value="all">All connected</option></select></label>
              <button type="button" className="la-text-button" onClick={() => { setFocusNodeId(""); setFocusDegree("all"); setSearch(""); setTypeFilter("All"); setConnectionFilter("All"); setActivityFilter("All"); setTargetOnly(false); }}>Reset filters</button>
              <div className="la-sidebar-summary"><span>{visibleData.nodes.length} visible nodes</span><span>{visibleData.edges.length} visible connections</span></div>
            </div>
          </aside>

          <div className="la-main-column">
            <section className="la-graph-panel">
              <div className="la-graph-toolbar">
                <div className="la-segmented">
                  <button className={graphType === "network" ? "is-active" : ""} onClick={() => setGraphType("network")}><HubOutlinedIcon /> Network</button>
                  <button className={graphType === "visual" ? "is-active" : ""} onClick={() => setGraphType("visual")}><AccountTreeOutlinedIcon /> Visual</button>
                </div>
                <div className="la-segmented">
                  <button className={viewMode === "all" ? "is-active" : ""} onClick={() => { setViewMode("all"); setPlaying(false); }}>See All</button>
                  <button className={viewMode === "timeline" ? "is-active" : ""} onClick={() => setViewMode("timeline")}><TimelineOutlinedIcon /> Timeline</button>
                </div>
                {graphType === "network" && <label className="la-physics-toggle"><input type="checkbox" checked={physics} onChange={(e) => setPhysics(e.target.checked)} /><span>Physics</span></label>}
                <div className="la-zoom-controls">
                  <button onClick={() => setZoom((z) => clamp(z * 1.12, 0.25, 2.2))} title="Zoom in"><ZoomInOutlinedIcon /></button>
                  <button onClick={() => setZoom((z) => clamp(z / 1.12, 0.25, 2.2))} title="Zoom out"><ZoomOutOutlinedIcon /></button>
                  <button onClick={fitGraph} title="Fit graph"><FitScreenOutlinedIcon /></button>
                </div>
              </div>

              {viewMode === "timeline" && (
                <div className="la-timeline-panel">
                  <div className="la-timeline-controls">
                    <button onClick={() => setTimelineValue(timelineBounds.min)} title="Beginning"><FirstPageOutlinedIcon /></button>
                    <button onClick={() => nudgeTimeline(-1)} title="Previous"><SkipPreviousOutlinedIcon /></button>
                    <button className="la-play-button" onClick={() => setPlaying((v) => !v)}>{playing ? <PauseOutlinedIcon /> : <PlayArrowOutlinedIcon />}</button>
                    <button onClick={() => nudgeTimeline(1)} title="Next"><SkipNextOutlinedIcon /></button>
                    <button onClick={() => setTimelineValue(timelineBounds.max)} title="End"><LastPageOutlinedIcon /></button>
                    <strong>{formatDateTime(timelineValue)}</strong>
                  </div>
                  <input className="la-timeline-range" type="range" min={timelineBounds.min} max={timelineBounds.max} step={Math.max(60000, stepMs / 10)} value={timelineValue} onChange={(e) => setTimelineValue(Number(e.target.value))} />
                  <div className="la-timeline-options">
                    <label><span>Granularity</span><select value={timelineGranularity} onChange={(e) => setTimelineGranularity(e.target.value)}><option>Auto</option><option>Hour</option><option>Day</option><option>Week</option><option>Month</option></select></label>
                    <label><span>Playback</span><select value={playbackSpeed} onChange={(e) => setPlaybackSpeed(Number(e.target.value))}><option value={0.5}>0.5x</option><option value={1}>1x</option><option value={2}>2x</option><option value={5}>5x</option></select></label>
                    <span>Resolved step: {resolvedGranularity}</span>
                  </div>
                </div>
              )}

              <div
                className={`la-graph-stage ${panning ? "is-panning" : ""}`}
                ref={stageRef}
                onPointerDown={onStagePointerDown}
                onPointerMove={onStagePointerMove}
                onPointerUp={onStagePointerUp}
                onPointerCancel={onStagePointerUp}
                onPointerLeave={onStagePointerUp}
                onWheel={onWheel}
              >
                {!visibleData.nodes.length ? (
                  <div className="la-empty-state"><AccountTreeOutlinedIcon /><h3>No nodes to display</h3><p>Upload a CSV, add an item, or load one of the fictional sample graphs.</p></div>
                ) : (
                  <div className="la-graph-world" style={{ width: GRAPH_WIDTH, height: GRAPH_HEIGHT, transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
                    <svg className="la-edge-layer" width={GRAPH_WIDTH} height={GRAPH_HEIGHT} viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}>
                      <defs><marker id="la-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" /></marker></defs>
                      {visibleData.edges.map((edge) => {
                        const a = positionedMap.get(edge.from);
                        const b = positionedMap.get(edge.to);
                        if (!a || !b) return null;
                        const midX = (a.px + b.px) / 2;
                        const midY = (a.py + b.py) / 2;
                        return (
                          <g className={`la-edge ${edge.connectionType === "Unauthorized" ? "is-alert" : ""}`} key={edge.id}>
                            <line x1={a.px} y1={a.py} x2={b.px} y2={b.py} markerEnd={edge.directed ? "url(#la-arrow)" : undefined} />
                            <text x={midX} y={midY - 7} textAnchor="middle">{edge.connectionType}</text>
                          </g>
                        );
                      })}
                    </svg>
                    {positionedNodes.map((node) => {
                      const Icon = iconForType(node.type);
                      return (
                        <button
                          type="button"
                          key={node.id}
                          className={`la-node ${graphType === "visual" ? "la-node-card" : "la-node-network"} la-type-${TYPE_CLASS[node.type] || "other"} ${node.mainTarget ? "is-main-target" : ""} ${selectedNodeId === node.id ? "is-selected" : ""}`}
                          style={{ left: node.px, top: node.py }}
                          onPointerDown={(e) => onNodePointerDown(e, node)}
                          onClick={(e) => { e.stopPropagation(); setSelectedNodeId(node.id); }}
                          title={node.identifier}
                        >
                          <span className="la-node-icon"><Icon /></span>
                          {graphType === "visual" ? (
                            <span className="la-node-copy"><strong>{node.identifier}</strong><small>{node.type}{node.label ? ` • ${node.label}` : ""}</small></span>
                          ) : <span className="la-node-network-label">{node.identifier}</span>}
                          {node.mainTarget && <span className="la-target-marker"><StarOutlinedIcon /></span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="la-graph-footer"><span>Drag nodes to refine the layout (main targets remain centered in Visual view). Drag empty canvas to pan. Scroll to zoom.</span><span>{Math.round(zoom * 100)}%</span></div>
            </section>

            {overlaps.length > 0 && (
              <section className="la-overlap-panel">
                <div className="la-section-heading"><div><span className="la-section-kicker">Automatic analysis</span><h2>Presence Overlaps</h2></div><p>People connected to the same physical place during overlapping time windows.</p></div>
                <div className="la-overlap-list">
                  {overlaps.slice(0, 12).map((item) => <div className="la-overlap-card" key={item.id}><WarningAmberOutlinedIcon /><div><strong>{item.a?.identifier || "Person"} + {item.b?.identifier || "Person"}</strong><span>{item.place.identifier}</span><small>{formatDateTime(item.start)} — {formatDateTime(item.end)}</small></div></div>)}
                </div>
              </section>
            )}

            <section className="la-inventory-panel">
              <div className="la-section-heading"><div><span className="la-section-kicker">Current workspace</span><h2>Node Inventory</h2></div><p>Cards reflect the active filters above. Blank optional fields are omitted.</p></div>
              <div className="la-inventory-groups">
                {NODE_TYPES.map((type) => {
                  const nodes = visibleData.nodes.filter((n) => n.type === type);
                  if (!nodes.length) return null;
                  return (
                    <div className="la-inventory-group" key={type}>
                      <div className="la-inventory-group-title"><strong>{type}</strong><span>{nodes.length}</span></div>
                      <div className="la-node-grid">
                        {nodes.map((node) => {
                          const Icon = iconForType(node.type);
                          const nodeEdges = data.edges.filter((e) => e.from === node.id || e.to === node.id);
                          return (
                            <button type="button" className={`la-inventory-card ${node.mainTarget ? "is-main-target" : ""}`} key={node.id} onClick={() => setSelectedNodeId(node.id)}>
                              <div className="la-inventory-card-head"><span className={`la-mini-icon la-type-${TYPE_CLASS[node.type] || "other"}`}><Icon /></span><div><strong>{node.identifier}</strong>{node.label && <small>{node.label}</small>}</div>{node.mainTarget && <span className="la-card-target">MAIN</span>}</div>
                              <div className="la-inventory-card-body">
                                {node.status && <span><b>Status</b>{node.status}</span>}
                                {node.tag && <span><b>Tag</b>{node.tag}</span>}
                                {node.source && <span><b>Source</b>{node.source}</span>}
                                <span><b>Connections</b>{nodeEdges.length}</span>
                                {nodeEdges.length > 0 && <div className="la-card-connections">{nodeEdges.slice(0, 4).map((edge) => { const other = data.nodes.find((n) => n.id === (edge.from === node.id ? edge.to : edge.from)); return <em key={edge.id}>{edge.connectionType}: {other?.identifier || "Unknown"}</em>; })}{nodeEdges.length > 4 && <em>+{nodeEdges.length - 4} more</em>}</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="la-export-panel">
              <div><span className="la-section-kicker">Portable workspace</span><h2>Export</h2><p>Export the underlying data for later reconstruction or capture the current graph as a PNG.</p></div>
              <div className="la-export-actions">
                <button className="la-button" onClick={() => exportCSV("all")}><DownloadOutlinedIcon /> Export All CSV</button>
                <button className="la-button" onClick={() => exportCSV("visible")}><DownloadOutlinedIcon /> Export Visible CSV</button>
                <button className="la-button la-button-primary" onClick={exportPNG}><ImageOutlinedIcon /> Export Graph PNG</button>
              </div>
            </section>
          </div>
        </section>
      </div>

      {selectedNode && (
        <aside className="la-detail-drawer" aria-label="Node details">
          <div className="la-drawer-header"><div><span className="la-section-kicker">Node details</span><h2>{selectedNode.identifier}</h2></div><button onClick={() => setSelectedNodeId("")}><CloseOutlinedIcon /></button></div>
          <div className="la-drawer-actions">
            <button onClick={() => setModal({ type: "node", value: { ...selectedNode } })}><EditOutlinedIcon /> Edit</button>
            <button onClick={() => updateNode(selectedNode.id, { hidden: !selectedNode.hidden })}>{selectedNode.hidden ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />} {selectedNode.hidden ? "Show" : "Hide"}</button>
            <button onClick={() => { updateNode(selectedNode.id, { mainTarget: !selectedNode.mainTarget }); }}>{selectedNode.mainTarget ? <StarOutlinedIcon /> : <StarOutlineOutlinedIcon />} {selectedNode.mainTarget ? "Remove target" : "Make target"}</button>
            <button onClick={() => { setFocusNodeId(selectedNode.id); setFocusDegree("1"); setSelectedNodeId(""); }}><CenterFocusStrongOutlinedIcon /> Focus</button>
          </div>
          <div className="la-detail-list">
            <Detail label="Type" value={selectedNode.type} />
            <Detail label="Label" value={selectedNode.label} />
            <Detail label="Status" value={selectedNode.status} />
            <Detail label="Tag" value={selectedNode.tag} />
            <Detail label="Source" value={selectedNode.source} />
            <Detail label="Description / Notes" value={selectedNode.description} wide />
          </div>
          <div className="la-drawer-subsection"><div className="la-drawer-subhead"><h3>Connections</h3><button onClick={() => setModal({ type: "edge", value: { from: selectedNode.id } })}><AddOutlinedIcon /> Add</button></div>
            <div className="la-drawer-connections">{relationshipsForSelected.length ? relationshipsForSelected.map(({ edge, other }) => <button key={edge.id} onClick={() => setModal({ type: "edge", value: { ...edge } })}><strong>{edge.connectionType}</strong><span>{other?.identifier || "Unknown node"}</span>{edge.start && <small>{formatDateTime(edge.start)}{edge.end ? ` — ${formatDateTime(edge.end)}` : " — active"}</small>}</button>) : <p>No connections recorded.</p>}</div>
          </div>
        </aside>
      )}

      {modal?.type === "node" && <NodeModal value={modal.value} nodes={data.nodes} onClose={() => setModal(null)} onSave={saveNodeFromModal} />}
      {modal?.type === "edge" && <ConnectionModal value={modal.value} nodes={data.nodes} onClose={() => setModal(null)} onSave={saveConnectionFromModal} />}
    </main>
  );
};


const Detail = ({ label, value, wide = false }) => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  return <div className={wide ? "is-wide" : ""}><span>{label}</span><strong>{value}</strong></div>;
};

const NodeModal = ({ value, nodes, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    ...makeNode(),
    id: "",
    ...(value || {}),
    connectTo: "",
    connectionType: "Associate",
    connectionStart: "",
    connectionEnd: "",
    directed: false,
    connectionNotes: "",
    evidence: "",
  }));
  const set = (field, next) => setForm((current) => ({ ...current, [field]: next }));
  return (
    <div className="la-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="la-modal" role="dialog" aria-modal="true">
        <header><div><span className="la-section-kicker">{value?.id ? "Edit node" : "New node"}</span><h2>{value?.id ? value.identifier : "Add Item"}</h2></div><button onClick={onClose}><CloseOutlinedIcon /></button></header>
        <div className="la-modal-body">
          <div className="la-form-grid">
            <Field label="Identifier" required><input value={form.identifier} onChange={(e) => set("identifier", e.target.value)} placeholder="Name or unique identifier" /></Field>
            <Field label="Type"><select value={form.type} onChange={(e) => set("type", e.target.value)}>{NODE_TYPES.map((type) => <option key={type}>{type}</option>)}</select></Field>
            <Field label="Label / Role"><input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Optional display label" /></Field>
            <Field label="Status"><input value={form.status} onChange={(e) => set("status", e.target.value)} placeholder="Authorized, active, review..." /></Field>
            <Field label="Source"><input value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="Optional source" /></Field>
            <Field label="Tag"><input value={form.tag} onChange={(e) => set("tag", e.target.value)} placeholder="Optional analyst tag" /></Field>
            <Field label="Description / Notes" wide><textarea value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
            <label className="la-check la-form-check"><input type="checkbox" checked={form.mainTarget} onChange={(e) => set("mainTarget", e.target.checked)} /> Main target / focus object</label>
          </div>
          {!value?.id && nodes.length > 0 && <div className="la-modal-subsection"><h3>Optional first connection</h3><div className="la-form-grid">
            <Field label="Connect to"><select value={form.connectTo} onChange={(e) => set("connectTo", e.target.value)}><option value="">No connection</option>{nodes.map((n) => <option key={n.id} value={n.id}>{n.identifier}</option>)}</select></Field>
            <Field label="Connection type"><select value={form.connectionType} onChange={(e) => set("connectionType", e.target.value)}>{CONNECTION_TYPES.map((type) => <option key={type}>{type}</option>)}</select></Field>
            <Field label="Connection start"><input type="datetime-local" value={toLocalInput(form.connectionStart)} onChange={(e) => set("connectionStart", e.target.value)} /></Field>
            <Field label="Connection end"><input type="datetime-local" value={toLocalInput(form.connectionEnd)} onChange={(e) => set("connectionEnd", e.target.value)} /><small>Leave blank for an active/open-ended connection.</small></Field>
            <label className="la-check la-form-check"><input type="checkbox" checked={form.directed} onChange={(e) => set("directed", e.target.checked)} /> Directional relationship</label>
            <Field label="Connection notes" wide><textarea value={form.connectionNotes} onChange={(e) => set("connectionNotes", e.target.value)} /></Field>
            <Field label="Evidence / Source Note" wide><textarea value={form.evidence} onChange={(e) => set("evidence", e.target.value)} /></Field>
          </div></div>}
        </div>
        <footer><button className="la-button" onClick={onClose}>Cancel</button><button className="la-button la-button-primary" disabled={!form.identifier.trim()} onClick={() => onSave(form)}>Save Item</button></footer>
      </section>
    </div>
  );
};

const ConnectionModal = ({ value, nodes, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({ ...makeEdge(), id: "", ...(value || {}) }));
  const set = (field, next) => setForm((current) => ({ ...current, [field]: next }));
  return (
    <div className="la-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="la-modal la-modal-connection" role="dialog" aria-modal="true">
        <header><div><span className="la-section-kicker">Relationship</span><h2>{value?.id ? "Edit Connection" : "Add Connection"}</h2></div><button onClick={onClose}><CloseOutlinedIcon /></button></header>
        <div className="la-modal-body"><div className="la-form-grid">
          <Field label="From" required><select value={form.from} onChange={(e) => set("from", e.target.value)}><option value="">Select node</option>{nodes.map((n) => <option key={n.id} value={n.id}>{n.identifier}</option>)}</select></Field>
          <Field label="To" required><select value={form.to} onChange={(e) => set("to", e.target.value)}><option value="">Select node</option>{nodes.map((n) => <option key={n.id} value={n.id}>{n.identifier}</option>)}</select></Field>
          <Field label="Connection type"><select value={form.connectionType} onChange={(e) => set("connectionType", e.target.value)}>{CONNECTION_TYPES.map((type) => <option key={type}>{type}</option>)}</select></Field>
          <label className="la-check la-form-check"><input type="checkbox" checked={form.directed} onChange={(e) => set("directed", e.target.checked)} /> Directional relationship</label>
          <Field label="Connection start"><input type="datetime-local" value={toLocalInput(form.start)} onChange={(e) => set("start", e.target.value)} /></Field>
          <Field label="Connection end"><input type="datetime-local" value={toLocalInput(form.end)} onChange={(e) => set("end", e.target.value)} /><small>Blank means the connection remains active.</small></Field>
          <Field label="Connection notes" wide><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          <Field label="Evidence / Source Note" wide><textarea value={form.evidence} onChange={(e) => set("evidence", e.target.value)} /></Field>
        </div></div>
        <footer><button className="la-button" onClick={onClose}>Cancel</button><button className="la-button la-button-primary" disabled={!form.from || !form.to || form.from === form.to} onClick={() => onSave(form)}>Save Connection</button></footer>
      </section>
    </div>
  );
};

const Field = ({ label, children, required = false, wide = false }) => <label className={`la-field ${wide ? "is-wide" : ""}`}><span>{label}{required && <em>Required</em>}</span>{children}</label>;

export { LinkAnalysis };
export default LinkAnalysis;
